import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { getJournalById } from "api/creditjnl.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { formatDate, formatNZD, printPage } from "common/utils";
import { PRINT_MARGIN } from "components/layouts/consts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useState } from "react";
import type { JournalDataEntryForm } from "types/model.types";

interface IAdminCreditJournalPreviewProps {
  id: number;
}
const AdminCreditJournalPreview: React.FC<IAdminCreditJournalPreviewProps> = ({
  id,
}) => {
  const [exporting, setExporting] = useState(false);
  const [initialData, setInitialData] = useState<JournalDataEntryForm | null>(
    null,
  );
  useEffect(() => {
    if (!id) return;

    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await safeApiCall(getJournalById(id));
        if (response && mounted) setInitialData(response);
      } catch (err) {
        logError(err);
        notifications.show({
          message: "Could not fetch journal details.",
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [id]);

  const exportToPDF = async () => {
    setExporting(true);
    const elementsToHide = document.querySelectorAll(".no-print");
    try {
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
      const element = document.getElementById("preview-content");
      if (!element) return;

      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const usableWidth = width - PRINT_MARGIN * 2;
      const usableHeight = (canvas.height * usableWidth) / canvas.width;

      pdf.addImage(
        imgData,
        "PNG",
        PRINT_MARGIN,
        PRINT_MARGIN,
        usableWidth,
        usableHeight,
      );
      pdf.save(`Order_${id}.pdf`);

      notifications.show({
        title: "PDF Exported",
        message: "Your order has been saved as a PDF.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Warn,
      });
    } catch (error) {
      notifications.show({
        title: "Export Failed",
        message: "Something went wrong during PDF export.",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
      console.error(error);
    } finally {
      setExporting(false);
      elementsToHide?.forEach((el) => {
        (el as HTMLElement).style.display = "none";
      });
    }
  };
  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".no-print");
    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "none";
    });

    const preview = document.getElementById("preview-content");
    if (!preview) {
      return;
    }
    printPage(preview.innerHTML);

    elementsToHide?.forEach((el) => {
      (el as HTMLElement).style.display = "";
    });
  };
  return (
    <>
      <div className="sub-title">
        *** Credit Journal #{initialData?.summary.id} ***
      </div>
      <table className="print-table">
        <tbody>
          <tr>
            <td className="leftCol">
              <span className="rcl">Date Ordered:</span>
              <span className="rcv">
                {formatDate(
                  initialData?.summary.createdOn ?? new Date().toDateString(),
                )}
              </span>
            </td>
            <td className="rightCol">
              <div>
                <span className="rcl">Entered:</span>
                <span className="rcv">
                  {formatNZD(initialData?.summary.entered ?? 0)}
                </span>
              </div>
              <div>
                <span className="rcl">Expected:</span>
                <span className="rcv">
                  {formatNZD(initialData?.summary.expected ?? 0)}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="spacer padTop padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            <th className="nowrap">Invoice #</th>
            <th className="nowrap">Debtor</th>
            <th className="description">Name</th>
            <th className="money">Amount</th>
          </tr>
        </thead>
        <tbody>
          {initialData?.entries.map((item) => (
            <tr key={item.id}>
              <td className="nowrap">{item.invoiceId}</td>
              <td className="nowrap">{item.debtorId}</td>
              <td className="description">{item.debtorName ?? 0}</td>
              <td className="money">{formatNZD(item.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
export default AdminCreditJournalPreview;
